import React, { useCallback } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  useMediaQuery,
  useTheme,
  Stack,
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { useAuth } from '../../app/providers/AuthProvider';
import { useAppTheme } from '../../app/providers/ThemeProvider';
import {
  getPrimaryNavigationItems,
  getSecondaryNavigationItems,
} from './NavigationConfig';
import UserAvatar from '../UserAvatar';

interface NavDrawerProps {
  open: boolean;
  onClose: () => void;
}

const NavDrawer = React.memo<NavDrawerProps>(({ open, onClose }) => {
  const { user, profile } = useAuth();
  const location = useLocation();
  const muiTheme = useTheme();
  const { customTokens: tokens } = useAppTheme();

  // Check if mobile breakpoint
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const isAuthenticated = Boolean(user);
  const isAdmin = profile?.isAdmin ?? false;

  const primaryItems = getPrimaryNavigationItems(isAuthenticated, isAdmin);
  const secondaryItems = getSecondaryNavigationItems(isAuthenticated, isAdmin);

  // Handle drawer close with proper focus management
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    },
    [handleClose],
  );

  // Handle list item click
  const handleItemClick = useCallback(() => {
    if (isMobile) {
      handleClose();
    }
  }, [isMobile, handleClose]);

  const drawerWidth = 280;

  const drawerContent = (
    <Box
      sx={{
        width: drawerWidth,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        pt: tokens.spacing[2],
      }}
      role="navigation"
      aria-label="Main navigation"
      onKeyDown={handleKeyDown}
    >
      {/* Header */}
      <Box sx={{ px: tokens.spacing[4], pb: tokens.spacing[3] }}>
        <Typography
          variant="h6"
          component="div"
          sx={{
            fontWeight: tokens.typography.fontWeight.bold,
            fontSize: tokens.typography.fontSize.lg,
            color: 'primary.main',
          }}
        >
          Penticton Dev Events
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mt: tokens.spacing[1],
          }}
        >
          Community Platform
        </Typography>
      </Box>

      <Divider />

      {/* Primary Navigation */}
      <List
        sx={{
          flexGrow: 1,
          pt: tokens.spacing[2],
          px: tokens.spacing[2],
        }}
      >
        {primaryItems.map(item => {
          const isActive = location.pathname === item.path;

          return (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                component={RouterLink}
                to={item.path}
                onClick={handleItemClick}
                selected={isActive}
                sx={{
                  borderRadius: tokens.radii.md,
                  mb: tokens.spacing[1],
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'primary.contrastText',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    borderRadius: tokens.radii.md,
                  },
                  transition: `all ${tokens.motion.duration.normal} ${tokens.motion.easing.ease}`,
                }}
                aria-current={isActive ? 'page' : undefined}
              >
                {item.icon && (
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: isActive ? 'primary.contrastText' : 'action.active',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                )}
                <ListItemText
                  primary={item.label}
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontWeight: isActive
                        ? tokens.typography.fontWeight.semibold
                        : tokens.typography.fontWeight.medium,
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Secondary Navigation */}
      {secondaryItems.length > 0 && (
        <>
          <Divider />
          <List sx={{ px: tokens.spacing[2], pb: tokens.spacing[2] }}>
            {secondaryItems.map(item => {
              const isActive = location.pathname === item.path;

              return (
                <ListItem key={item.path} disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to={item.path}
                    onClick={handleItemClick}
                    selected={isActive}
                    sx={{
                      borderRadius: tokens.radii.md,
                      mb: tokens.spacing[1],
                      '&.Mui-selected': {
                        backgroundColor: 'action.selected',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      },
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        borderRadius: tokens.radii.md,
                      },
                      transition: `all ${tokens.motion.duration.normal} ${tokens.motion.easing.ease}`,
                    }}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {item.icon && (
                      <ListItemIcon
                        sx={{
                          minWidth: 40,
                          color: 'action.active',
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                    )}
                    <ListItemText
                      primary={item.label}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontWeight: isActive
                            ? tokens.typography.fontWeight.semibold
                            : tokens.typography.fontWeight.medium,
                          fontSize: tokens.typography.fontSize.sm,
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </>
      )}

      {/* User Info Footer */}
      {user && (
        <>
          <Divider />
          <Box sx={{ p: tokens.spacing[3] }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <UserAvatar
                displayName={profile?.displayName}
                photoURL={user.photoURL}
                email={profile?.email}
                sx={{ width: 40, height: 40 }}
              />
              <Box>
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                >
                  Signed in as
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: tokens.typography.fontWeight.medium }}
                >
                  {profile?.displayName ?? user.email ?? 'Member'}
                </Typography>
                {profile?.isAdmin && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'primary.main',
                      textTransform: 'capitalize',
                      fontWeight: 600,
                    }}
                  >
                    Platform Admin
                  </Typography>
                )}
              </Box>
            </Stack>
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={handleClose}
      variant={isMobile ? 'temporary' : 'temporary'}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile
      }}
      sx={{
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          borderRight: `1px solid ${muiTheme.palette.divider}`,
          background: muiTheme.palette.background.paper,
        },
      }}
      // ARIA attributes for accessibility
      PaperProps={{
        'aria-label': 'Navigation drawer',
        role: 'navigation',
      }}
    >
      {drawerContent}
    </Drawer>
  );
});

NavDrawer.displayName = 'NavDrawer';

export default NavDrawer;
